/**
 * Variant Dropdown with Images Component
 * Custom dropdown that displays variant images alongside option values
 */

if (!customElements.get('variant-dropdown-with-images')) {
  class VariantDropdownWithImages extends HTMLElement {
    constructor() {
      super();
      this.isOpen = false;
    }

    connectedCallback() {
      // Query elements after they're in the DOM
      this.button = this.querySelector('.variant-dropdown__button');
      this.menu = this.querySelector('.variant-dropdown__menu');
      this.options = this.querySelectorAll('.variant-dropdown__option');
      this.hiddenSelect = this.querySelector('select');
      this.selectedDisplay = this.querySelector('.variant-dropdown__selected');
      this.icon = this.querySelector('.variant-dropdown__icon');
      
      console.log('Variant dropdown connected:', {
        button: !!this.button,
        menu: !!this.menu,
        options: this.options.length,
        select: !!this.hiddenSelect
      });
      
      this.init();
    }

    init() {
      if (!this.button || !this.menu || !this.hiddenSelect) {
        console.error('Variant dropdown: Missing required elements', {
          button: !!this.button,
          menu: !!this.menu,
          select: !!this.hiddenSelect
        });
        return;
      }
      
      console.log('Variant dropdown initialized successfully');
      
      // Toggle dropdown on button click
      this.button.addEventListener('click', (e) => {
        console.log('Button clicked');
        e.preventDefault();
        e.stopPropagation();
        this.toggle();
      });
      
      // Handle option selection
      this.options.forEach(option => {
        option.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (option instanceof HTMLElement) {
            this.selectOption(option);
          }
        });
      });
      
      // Close on outside click
      document.addEventListener('click', (e) => {
        if (e.target instanceof Node && !this.contains(e.target) && this.isOpen) {
          this.close();
        }
      });
      
      // Close on escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.close();
        }
      });
      
      // Keyboard navigation
      this.button.addEventListener('keydown', /** @param {KeyboardEvent} e */ (e) => {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          this.open();
        }
      });
      
      // Menu keyboard navigation
      this.menu.addEventListener('keydown', /** @param {KeyboardEvent} e */ (e) => {
        this.handleKeyboardNavigation(e);
      });
    }

    toggle() {
      console.log('Toggle called, isOpen:', this.isOpen);
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    }

    open() {
      if (this.isOpen || !this.menu || !this.button) return;
      
      console.log('Opening dropdown');
      this.isOpen = true;
      this.menu.classList.remove('hidden');
      this.button.setAttribute('aria-expanded', 'true');
      
      // Rotate icon
      if (this.icon instanceof HTMLElement) {
        this.icon.style.transform = 'rotate(180deg)';
      }
      
      // Position dropdown
      this.positionMenu();
    }

    close() {
      if (!this.isOpen || !this.menu || !this.button) return;
      
      this.isOpen = false;
      this.menu.classList.add('hidden');
      this.button.setAttribute('aria-expanded', 'false');
      
      // Reset icon rotation
      if (this.icon instanceof HTMLElement) {
        this.icon.style.transform = 'rotate(0deg)';
      }
    }

    /**
     * Select a dropdown option
     * @param {HTMLElement} optionElement 
     */
    selectOption(optionElement) {
      if (!this.hiddenSelect) return;
      
      const value = optionElement.dataset.value;
      
      if (!value) return;
      
      // Update hidden select
      const selectOption = Array.from(this.hiddenSelect.options).find(
        opt => opt.value === value
      );
      
      if (selectOption) {
        this.hiddenSelect.value = value;
        
        // Trigger change event on the select element
        const changeEvent = new Event('change', { bubbles: true });
        this.hiddenSelect.dispatchEvent(changeEvent);
      }
      
      // Update visual selection
      this.updateVisualSelection(optionElement);
      
      // Close dropdown
      this.close();
    }

    /**
     * Update visual selection state
     * @param {HTMLElement} selectedOption 
     */
    updateVisualSelection(selectedOption) {
      if (!this.selectedDisplay) return;
      
      // Remove selected state from all options
      this.options.forEach(opt => {
        opt.classList.remove('bg-[#F9FAFB]');
        opt.setAttribute('aria-selected', 'false');
      });
      
      // Add selected state to the clicked option
      selectedOption.classList.add('bg-[#F9FAFB]');
      selectedOption.setAttribute('aria-selected', 'true');
      
      // Update button content
      const imageHtml = selectedOption.querySelector('.variant-dropdown__option-image')?.outerHTML || '';
      const textContainer = selectedOption.querySelector('.flex.flex-col');
      
      if (textContainer) {
        // Clone the container and update classes for selected display
        const clonedContainer = textContainer.cloneNode(true);
        if (clonedContainer instanceof HTMLElement) {
          // Update description classes from option to selected style
          const desc = clonedContainer.querySelector('.variant-dropdown__option-desc');
          if (desc instanceof HTMLElement) {
            desc.className = 'variant-dropdown__selected-desc text-[11px] text-gray-500 leading-tight line-clamp-2';
          }
          
          this.selectedDisplay.innerHTML = `
            ${imageHtml}
            ${clonedContainer.outerHTML}
          `;
        }
      } else {
        // Fallback if structure is different
        const text = selectedOption.querySelector('.variant-dropdown__option-text')?.textContent || '';
        this.selectedDisplay.innerHTML = `
          ${imageHtml}
          <div class="flex flex-col gap-0.5">
            <span class="variant-dropdown__text text-[16px] font-twkLausanne">${text}</span>
          </div>
        `;
      }
    }

    positionMenu() {
      if (!this.menu || !this.button) return;
      if (!(this.menu instanceof HTMLElement)) return;
      
      // Reset any previous positioning
      this.menu.style.top = '';
      this.menu.style.bottom = '';
      
      // Check if dropdown would overflow viewport bottom
      const rect = this.button.getBoundingClientRect();
      const menuHeight = this.menu.offsetHeight;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      // If not enough space below and more space above, open upward
      if (spaceBelow < menuHeight && spaceAbove > spaceBelow) {
        this.menu.style.bottom = '100%';
        this.menu.style.top = 'auto';
        this.menu.style.marginBottom = '4px';
    /**
     * Handle keyboard navigation in dropdown menu
     * @param {KeyboardEvent} e 
     */
        this.menu.style.marginTop = '0';
      }
    }

    handleKeyboardNavigation(e) {
      const currentFocus = document.activeElement;
      const optionsArray = Array.from(this.options);
      
      if (!currentFocus) return;
      
      const currentIndex = optionsArray.indexOf(currentFocus);
      
      switch(e.key) {
        case 'ArrowDown':
          e.preventDefault();
          const nextIndex = currentIndex < optionsArray.length - 1 ? currentIndex + 1 : 0;
          if (optionsArray[nextIndex] instanceof HTMLElement) {
            optionsArray[nextIndex].focus();
          }
          break;
          
        case 'ArrowUp':
          e.preventDefault();
          const prevIndex = currentIndex > 0 ? currentIndex - 1 : optionsArray.length - 1;
          if (optionsArray[prevIndex] instanceof HTMLElement) {
            optionsArray[prevIndex].focus();
          }
          break;
          
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (currentFocus instanceof HTMLElement && optionsArray.includes(currentFocus)) {
            this.selectOption(currentFocus);
          }
          break;
          
        case 'Home':
          e.preventDefault();
          if (optionsArray[0] instanceof HTMLElement) {
            optionsArray[0].focus();
          }
          break;
          
        case 'End':
          e.preventDefault();
          const lastOption = optionsArray[optionsArray.length - 1];
          if (lastOption instanceof HTMLElement) {
            lastOption.focus();
          }
          break;
      }
    }
  }

  customElements.define('variant-dropdown-with-images', VariantDropdownWithImages);
}
